<template>
    <div class="space-y-4">
        <!-- Section Header -->
        <div class="flex items-center justify-between">
            <h2 class="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {{ $t('tracking.todaysChores.title') }}
            </h2>
            <div v-if="todaysChores.length > 0" class="flex items-center space-x-2">
                <Tag :value="todaysChores.length.toString()" severity="success" />
                <span class="text-sm text-gray-500 dark:text-gray-400">
                    {{ $t('tracking.todaysChores.totalXp', { xp: totalXpToday }) }}
                </span>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="space-y-3">
            <div v-for="n in 2" :key="n" class="animate-pulse">
                <div class="bg-gray-100 dark:bg-gray-800 h-24 rounded-lg"></div>
            </div>
        </div>

        <!-- Empty State -->
        <Card v-else-if="todaysChores.length === 0" class="text-center">
            <template #content>
                <div class="py-6">
                    <div class="text-gray-400 dark:text-gray-600 text-4xl mb-3">📋</div>
                    <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {{ $t('tracking.todaysChores.empty.title') }}
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                        {{ $t('tracking.todaysChores.empty.description') }}
                    </p>
                </div>
            </template>
        </Card>

        <!-- Today's Chores List - Compact View -->
        <Card v-else>
            <template #content>
                <div class="space-y-3">
                    <div v-for="chore in todaysChores" :key="chore.id || 'unknown'"
                        class="flex items-center justify-between py-2 px-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                        <div class="flex items-center space-x-3">
                            <div>
                                <span class="font-medium text-gray-900 dark:text-gray-100">
                                    {{ chore.choreName }}
                                </span>
                                <div class="flex items-center space-x-2 mt-1">
                                    <Badge :value="chore.categoryName || 'Unknown'" size="small" severity="secondary" />
                                    <span class="text-xs text-gray-500 dark:text-gray-400">
                                        {{ formatTime(chore.performedAt) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center space-x-2">
                            <span class="text-sm font-medium text-green-600 dark:text-green-400">
                                {{ getTotalXp(chore) }} XP
                            </span>
                            <Button v-if="canDelete(chore)" @click="confirmDelete(chore)" icon="pi pi-trash"
                                size="small" text severity="secondary" class="p-1" :disabled="deleting"
                                v-tooltip="$t('common.delete')" />
                        </div>
                    </div>
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { PerformedChoreDto } from '@/client';
import { useI18n } from 'vue-i18n';
import { useConfirm } from 'primevue/useconfirm';

const props = defineProps<{
    loading: boolean;
    todaysChores: PerformedChoreDto[];
    currentUserId?: string;
    deleting?: boolean;
}>();

const emit = defineEmits<{
    'delete-chore': [choreId: string];
}>();

const { t } = useI18n();
const confirm = useConfirm();

const totalXpToday = computed(() => {
    return props.todaysChores.reduce((total, chore) => {
        return total + (chore.participants?.reduce((choreTotal, participant) => {
            return choreTotal + (participant.totalXpAwarded || 0);
        }, 0) || 0);
    }, 0);
});

const formatTime = (date: Date | null | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit'
    });
};

const getTotalXp = (chore: PerformedChoreDto) => {
    return chore.participants?.reduce((total, participant) => {
        return total + (participant.totalXpAwarded || 0);
    }, 0) || 0;
};

const canDelete = (chore: PerformedChoreDto) => {
    if (!props.currentUserId || !chore.participants) return false;
    return chore.participants.some(p => p.userId === props.currentUserId);
};

const confirmDelete = (chore: PerformedChoreDto) => {
    confirm.require({
        message: t('history.delete.confirmMessage', { choreName: chore.choreName || '' }),
        header: t('history.delete.confirmTitle'),
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: t('common.cancel'),
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: t('common.delete'),
            severity: 'danger'
        },
        accept: () => {
            if (chore.id) {
                emit('delete-chore', chore.id);
            }
        }
    });
};
</script>